import type { Schema, NodeType, Node, Attrs } from '@atlaskit/editor-prosemirror/model';
import { Mark } from '@atlaskit/editor-prosemirror/model';
import type { Content as MdastNodes } from 'mdast';

import { unsupportedMarkdownContent } from './handlers/unsupportedMarkdownContent';

/**
 * A single mdast-to-ProseMirror handler function.
 * The `node` parameter is typed as the full `Content` union; narrow inside as needed.
 */
export type Handler = (state: State, node: MdastNodes, index: number) => void;

/** Map of mdast node type names to handler functions. */
export type Handlers = {
	[key: string]: Handler | undefined;
};

interface StackEntry {
	attrs: Attrs | null;
	content: Node[];
	marks: readonly Mark[];
	type: NodeType;
}

// withText is a stable ProseMirror internal API for text nodes that is not in
// the public TypeScript types. We cast through the PM Node type to access it.
type TextNodeInternal = Node & { withText: (text: string) => Node };

function maybeMerge(a: Node, b: Node): Node | undefined {
	if (a.isText && b.isText && Mark.sameSet(a.marks, b.marks)) {
		return (a as TextNodeInternal).withText((a.text ?? '') + (b.text ?? ''));
	}
}

export class State {
	stack: StackEntry[] = [];
	rowIndex: number | null = null;

	constructor(
		public schema: Schema,
		public handlers: Handlers,
		public sourceMarkdown?: string,
	) {}

	top(): StackEntry {
		return this.stack[this.stack.length - 1];
	}

	push(node: Node): void {
		if (this.stack.length) {
			this.top().content.push(node);
		}
	}

	/**
	 * Adds the given text to the current position in the document,
	 * using the current marks as styling.
	 */
	addText(text: string): void {
		if (!text) {
			return;
		}
		const top = this.top(),
			nodes = top.content,
			last = nodes[nodes.length - 1],
			node = this.schema.text(text, top.marks);

		let merged;
		if (last && (merged = maybeMerge(last, node))) {
			nodes[nodes.length - 1] = merged;
		} else {
			nodes.push(node);
		}
	}

	/** Adds the given mark to the set of active marks. */
	openMark(mark: Mark): void {
		const top = this.top();
		top.marks = mark.addToSet(top.marks);
	}

	/** Removes the given mark from the set of active marks. */
	closeMark(mark: Mark): void {
		const top = this.top();
		top.marks = mark.removeFromSet(top.marks);
	}

	/** Add a node at the current position. */
	addNode(type: NodeType, attrs: Attrs | null, content: Node[]): Node | null {
		const top = this.top();
		const node = type.createAndFill(attrs, content, top ? top.marks : []);
		if (!node) {
			return null;
		}
		this.push(node);
		return node;
	}

	/** Wrap subsequent content in a node of the given type. */
	openNode(type: NodeType, attrs: Attrs | null = null): void {
		this.stack.push({ type, attrs, content: [], marks: Mark.none });
	}

	/** Close and return the node that is currently on top of the stack. */
	closeNode(): Node | null {
		const info = this.stack.pop();
		if (!info) {
			throw new Error('No node open at the top of the stack');
		}
		return this.addNode(info.type, info.attrs, info.content);
	}

	parseTokens(nodes: MdastNodes[]): void {
		for (let i = 0; i < nodes.length; i++) {
			const node = nodes[i];
			const handler = this.handlers[node.type as string];
			if (!handler) {
				unsupportedMarkdownContent(this, node);
				continue;
			}
			handler(this, node, i);
		}
	}
}
